package uk.co.nickthecoder.kyd.nodes

import uk.co.nickthecoder.glok.backend.Texture
import uk.co.nickthecoder.glok.event.Key
import uk.co.nickthecoder.glok.event.MouseEvent
import uk.co.nickthecoder.kyd.Game
import uk.co.nickthecoder.kyd.Stage
import uk.co.nickthecoder.kyd.StageDescription
import uk.co.nickthecoder.kyd.util.Ticker

/**
 * There is only ever one [PlayActView] which is created shortly after the application starts, and is only
 * destroyed when the application ends.
 *
 */
open class PlayActView(

    game: Game

) : ActView(game) {

    private val keyDownSet = mutableSetOf<Key>()

    fun isKeyDown(key: Key) = keyDownSet.contains(key)

    init {
        focusAcceptable = true
        focusTraversable = true
        // Glok events don't work well with overlapping nodes.
        // So we have to pass events to each PlayStageView manually.
        onMouseClicked { event -> mouseClicked(event) }
        onMousePressed { event -> mousePressed(event) }
        onMouseReleased { event -> mouseReleased(event) }
        onMouseDragged { event -> mouseDragged(event) }

        onKeyPressed { event ->
            keyDownSet.add(event.key)
            act?.director?.onKeyPressed(event)
        }
        onKeyReleased { event ->
            keyDownSet.remove(event.key)
            act?.director?.onKeyReleased(event)
        }

        // Request a redraw each time the tick counter increases.
        Ticker.tickCounterProperty.addListener(requestRedrawListener)

        sceneProperty.addChangeListener { _, _, scene ->
            if (scene != null) requestFocus()
        }
    }

    // region == Public Methods ==

    // region == MouseEvents ==

    private fun mousePressed(event: MouseEvent) {
        for (view in children) {
            if ((view as? PlayStageView)?.mousePressed(event) == true) break
        }
    }

    private fun mouseReleased(event: MouseEvent) {
        for (view in children) {
            (view as? PlayStageView)?.mouseReleased(event)
        }
    }

    private fun mouseClicked(event: MouseEvent) {
        for (view in children) {
            if ((view as? PlayStageView)?.mouseClicked(event) == true) break
        }
    }

    private fun mouseDragged(event: MouseEvent) {
        for (view in children) {
            (view as? PlayStageView)?.mouseDragged(event)
        }
    }

    // endregion mouseEvents

    // endregion == Public Methods ==

    override fun createStageView(stage: Stage) = PlayStageView(stage)


    override fun bindWorldProperties(stageView: StageView, stageDescription: StageDescription) {
        if (stageDescription.linked) {
            super.bindWorldProperties(stageView, stageDescription)
        }
    }

    override fun toString() = "ActView with ${act?.stages?.size ?: 0} stages"

    companion object {

        /**
         * The [PlayActView] for the game being played.
         *
         */
        lateinit var instance: PlayActView
    }
}
